import React, { useState, useRef, useEffect, useCallback } from 'react';
import t from 'react-translate';
import { questionTextStyles, showMoreLessButtonStyles } from '../styles';

interface QuestionTextProps {
  question?: string;
}

const QuestionText = ({ question }: QuestionTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const [truncatedText, setTruncatedText] = useState('');
  const textRef = useRef<HTMLDivElement>(null);

  const createTestElement = (element: HTMLElement, computedStyle: CSSStyleDeclaration): HTMLElement => {
    const testElement = document.createElement('div');
    testElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: ${element.offsetWidth}px;
      font-family: ${computedStyle.fontFamily};
      font-size: ${computedStyle.fontSize};
      font-weight: ${computedStyle.fontWeight};
      line-height: ${computedStyle.lineHeight};
      text-align: ${computedStyle.textAlign};
      padding: ${computedStyle.padding};
      margin: ${computedStyle.margin};
    `;
    return testElement;
  };

  const calculateMaxHeight = (computedStyle: CSSStyleDeclaration): number => {
    const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
    return Math.floor(lineHeight * 5);
  };

  const findTruncatedText = (
    questionText: string,
    testElement: HTMLElement,
    maxHeight: number,
  ): string => {
    const words = questionText.split(' ');
    const showMoreText = ` ${t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.SHOW_MORE()}`;
    let truncated = '';

    for (let i = words.length - 1; i >= 0; i -= 1) {
      const testText = words.slice(0, i + 1).join(' ');
      testElement.textContent = testText + showMoreText;

      if (testElement.scrollHeight <= maxHeight) {
        truncated = testText;
        break;
      }
    }
    // Remove 3 last characters and add ...
    return `${truncated.slice(0, -3).trim()}...`;
  };

  const checkTextTruncation = useCallback((): void => {
    if (!question || !textRef.current) return;

    const element = textRef.current;
    const computedStyle = getComputedStyle(element);
    const maxHeight = calculateMaxHeight(computedStyle);

    const testElement = createTestElement(element, computedStyle);
    testElement.textContent = question;
    document.body.appendChild(testElement);

    const naturalHeight = testElement.scrollHeight;
    const needsTruncation = naturalHeight > maxHeight;

    if (needsTruncation) {
      setShouldTruncate(true);
      const truncated = findTruncatedText(question, testElement, maxHeight);
      setTruncatedText(truncated);
    } else {
      setShouldTruncate(false);
    }

    document.body.removeChild(testElement);
  }, [question]);

  useEffect(() => {
    checkTextTruncation();
  }, [checkTextTruncation]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!question) return null;

  return (
    <div css={questionTextStyles} ref={textRef}>
      {!shouldTruncate || isExpanded ? (
        <>
          {question}
          {shouldTruncate && isExpanded && (
            <button
              css={showMoreLessButtonStyles}
              onClick={toggleExpanded}
              type='button'
            >
              {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.SHOW_LESS()}
            </button>
          )}
        </>
      ) : (
        <>
          {truncatedText}
          <button
            css={showMoreLessButtonStyles}
            onClick={toggleExpanded}
            type='button'
          >
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.SHOW_MORE()}
          </button>
        </>
      )}
    </div>
  );
};

export default QuestionText;
